<template>
  <net-item
    v-if="treeNode && sceneNode"
    class="net-text__container"
    item-class="net-text"
    :uid="nodeUid"
    :size="size"
    :node-uid="nodeUid"
    :item-style="{ color: sceneNode.textColor || 'var(--color-text)' }"
    :class="{
      '-selected': selected,
      '-editable': props.editable,
    }"
    :min-height="minHeight"
    :min-width="30"
    :grid-size="1"
    :focussed="state.focussed"
    :selected="selected"
    :locked="treeNode.locked"
    @resize="onResize"
    @move="onMove"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @select="select($event)"
  >
    <net-input-text
      ref="inputElement"
      class="net-text__input"
      :style="{ fontSize: baseFontSize + 'px' }"
      :value="ctx.textValue"
      :stylable="sceneNode.data.stylable"
      :disabled="!editable"
      :selected="selected"
      :line-height="sceneNode.lineHeight ?? 1.15"
      :position="{ x: size.x, y: size.y }"
      :text-options="{
        textColor: sceneNode.textColor,
        textAlign: sceneNode.textAlign,
        fontFamily: sceneNode.fontFamily,
        lineHeight: sceneNode.lineHeight,
        fontSize: baseFontSize
      }"
      @update:value="ctx.textValue = $event"
      @update:text-option="updateTextOption"
      @focus="onFocus"
      @blur="onBlur"
      @enter-edit-mode="emit('update:tool', 'textedit')"
      @leave-edit-mode="emit('update:tool', 'pointer')"
    ></net-input-text>
  </net-item>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import { useElementBounding } from "@vueuse/core";

import { isEmpty } from "@/helpers/utility/Text";
import { isTextTreeNode } from "./sceneTextFunctions";

import NetItem from "@editor/net/NetItem.vue";
import NetInputText from "@editor/net/NetInputText.vue";

import { useUndo } from "@editor/store/undo/undo.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneText } from "./useSceneText";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { NetToolKey } from "@editor/net/NetTools";
import type { Uid } from "@/helpers/utility/Id";
import type { RotatableRect, SceneNodeItem, SceneNodeStyle } from "../scene/SceneNodeItem";
import type { TextOptions } from "@editor/net/netTextToolbarContext";

const props = withDefaults(defineProps<{
  nodeUid: Uid<"sceneNode">;
  editable?: boolean;
  size: RotatableRect;
  selection: CanvasSelection;
  tool?: NetToolKey;
}>(), {
  editable: false
});

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"sceneNode"> }): void;
  (e: "wheel", event: WheelEvent): void;
  (e: "move", delta: { x: number; y: number; gridX: number; gridY: number }): void;
  (e: "update:tool", tool: NetToolKey): void;
}>();

const sceneTree = useSceneTree();

const inputElement = ref<InstanceType<typeof NetInputText>>();

const sceneNodeStore = useSceneNodesStore();
const selectionStore = useSelectionStore();

const treeNode = computed(() => {
  const node = sceneTree.treeNodes.get(props.nodeUid);
  if (!node) return null;
  if (!isTextTreeNode(node)) return null;
  return node;
});
const sceneNode = computed(() => treeNode.value?.node ?? null);

const ctx = useSceneText(() => sceneNode.value?.id ?? null);

const textBounds = useElementBounding(() => inputElement.value?.mainElement, { updateTiming: "next-frame"});

function updateTextOption<K extends keyof TextOptions>(key: K, value: TextOptions[K]) {
  if (key == "fontSize") {
    baseFontSize.value = value as TextOptions["fontSize"];
    return;
  }
  return updateStyle(key, value);
}

function updateStyle<K extends keyof SceneNodeStyle>(key: K, value: SceneNodeItem[K]) {
  if (!sceneNode.value) return;
  sceneNodeStore.updateKey(sceneNode.value.id, key, value);
}

// ====================
// Focus and editing state

const state = reactive({
  focussed: false
});

const selected = computed(() => {
  return selectionStore.selectedItems.some((item) => item.type == "sceneNode" && item.id == sceneNode.value?.id);
});

function onFocus() {
  state.focussed = true;
  // if (!props.disabled) {
  //   select(props.element);
  // }
}
function onBlur() {
  state.focussed = false;
}

function select(item: { uid: Uid<"sceneNode"> }) {
  emit("select", item);
}

onMounted(() => {
  if (isEmpty(sceneNode.value?.data.text)) {
    inputElement.value?.switchToEditMode();
  }
});

// Remove empty text elements when deselected
watch(selected, (selected) => {
  if (!selected) {
    // Blur the input when deselected
    inputElement.value?.blur();

    const node = sceneNode.value;
    if (!node) return;

    // If the text is empty, delete it
    if (!node.data.connected && isEmpty(node.data.text)) {
      sceneNodeStore.delete({ id: node.id });
    }
  }
});

// ====================
// Drag and resize handlers

function onDragStart() {
  // Group the action to make sure the movement of all
  // targets can be undone at once
  useUndo().startGroup("model", "moveText");
}

function onDragEnd() {
  // Close the drag action undo group, including
  // the area update
  useUndo().endGroup("model");
}

function onResize(newSize: {
  x: number;
  y: number;
  height: number;
  width: number;
}) {
  if (!treeNode.value) return;
  sceneNodeStore.update({
    id: treeNode.value.id,
    x: newSize.x,
    y: newSize.y,
    height: newSize.height,
    width: newSize.width
  });
}
function onMove(delta: { x: number; y: number; gridX: number; gridY: number }) {
  emit("move", delta);
}

// ====================
// Mouse pressed state

const mousePressed = ref(false);
function onMouseDown() {
  mousePressed.value = true;
}
function onMouseUp() {
  mousePressed.value = false;
}
onMounted(() => {
  window.addEventListener("pointerdown", onMouseDown);
  window.addEventListener("pointerup", onMouseUp);
});
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", onMouseDown);
  window.removeEventListener("pointerup", onMouseUp);
});

// ====================
// Size and position

watch(textBounds.height, async (newHeight) => {
  if (mousePressed.value) return;
  if (!props.editable) return;
  if (!inputElement.value) return;
    if (sceneNode.value?.data.text === "") return;
  updateSizeFromContent();
});

function updateSizeFromContent() {
  if (!inputElement.value) return;
  const height = inputElement.value.mainElement?.offsetHeight || 0;
  if (height !== props.size.height) {
    if (!treeNode.value) return;
    sceneNodeStore.update({
      id: treeNode.value.id,
      height: height
    }, { _undoable: false } // resizing from content change is not undoable
    );
  }
}

watch(() => props.size, () => {
  nextTick(() => {
    if (sceneNode.value?.data.text === "") return;
    updateSizeFromContent();
  });
})


// ====================
// Font size

const sceneFontSize = computed(() => sceneTree.activeScene.fontSize);
const baseFontSize = computed({
  get() {
    if (!sceneNode.value) return sceneFontSize.value;
    return sceneFontSize.value * sceneNode.value.fontSizeRelative;
  },
  set(value: number | null | undefined) {
    if (!sceneNode.value) return;
    if (value == null) value = sceneFontSize.value;
    const relativeSize = value! / sceneFontSize.value;
    sceneNodeStore.updateKey(sceneNode.value.id, "fontSizeRelative", relativeSize);
  }
});

const minHeight = computed(() => {
  return (baseFontSize.value) * 1.2;
});
</script>

<style lang="scss">
.net-text {
  display: flex;
  justify-content: stretch;

  &__container {
    pointer-events: none;

    &.-editable {
      pointer-events: auto;
    }
  }

  &.is-selected {
    outline: 2px solid var(--color-primary);
  }

  &__input {
    width: 100%;
    grid-template-rows: 1fr;
    overflow: visible;
    height: min-content;
  }
}
</style>
